import * as React from 'react'

import { cn } from '@/lib/css-utils'

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  autocomplete?: string
  LeftIcon?: React.ComponentType<React.SVGProps<SVGSVGElement>>
  RightIcon?: React.ComponentType<React.SVGProps<SVGSVGElement>>
  maxLength?: number
}

const BaseInput = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, LeftIcon, RightIcon, onChange, maxLength, ...props }, ref) => {
    return (
      <input
        className={cn(
          'flex h-9 w-full rounded-lg border border-input-border bg-input-bg px-4 py-5 text-input-text text-md',
          'transition-colors file:border-0 file:bg-transparent file:font-medium hover:shadow-xs',
          'hover:border-input-hover-border placeholder:text-input-placeholder',
          'focus-visible:shadow-focused focus-visible:border-input-focus-border',
          'disabled:cursor-not-allowed disabled:bg-input-disabled-bg disabled:text-input-disabled-text ',
          'disabled:border-input-disabled-border',
          'focus:shadow-focused focus:border-input-focus-border:',
          'focus:outline-1 focus:outline-input-focus-border',
          LeftIcon && 'pl-10',
          RightIcon && 'pr-10',
          className,
        )}
        onChange={(e) => {
          const newInput = e.target.value
          if (
            maxLength &&
            newInput.length > (props.value as string)?.length &&
            newInput.length > maxLength
          )
            return
          onChange?.(e)
        }}
        ref={ref}
        {...props}
      />
    )
  },
)

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, LeftIcon, RightIcon, ...props }: InputProps, ref) => {
    if (!LeftIcon && !RightIcon) {
      return <BaseInput ref={ref} {...props} className={className} />
    }
    return (
      <div className="relative">
        {LeftIcon && (
          <LeftIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-copy-primary h-4 w-4" />
        )}
        <BaseInput
          ref={ref}
          {...props}
          LeftIcon={LeftIcon}
          RightIcon={RightIcon}
          className={className}
        />
        {RightIcon && (
          <RightIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 text-copy-primary h-4 w-4" />
        )}
      </div>
    )
  },
)

BaseInput.displayName = 'BaseInput'
Input.displayName = 'Input'

export { Input }
